import { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DiamondRingMark from '../../../shared/src/components/icons/DiamondRingMark';

interface CountdownSectionProps {
  weddingDate: string; // 'YYYY-MM-DD'
  timezone?: string; // IANA timezone, e.g. 'Asia/Hong_Kong'
}

/* ── Chinese date helpers (mirrors InvitationPage) ── */
const CN_DIGITS = ['〇', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
const CN_WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
const CN_MONTHS = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二'];

function toCnDigits(n: number): string {
  return String(n).split('').map(c => CN_DIGITS[Number(c)]!).join('');
}

function toCnDay(day: number): string {
  if (day <= 10) return CN_MONTHS[day]!;
  if (day < 20) return '十' + (day === 10 ? '' : CN_DIGITS[day - 10]!);
  if (day === 20) return '二十';
  if (day < 30) return '二十' + CN_DIGITS[day - 20]!;
  if (day === 30) return '三十';
  return '三十' + CN_DIGITS[day - 30]!;
}

function formatWeddingDate(dateStr: string, lang: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  if (isNaN(d.getTime())) return dateStr;

  if (lang === 'zh_hk') {
    const year = toCnDigits(d.getFullYear());
    const month = CN_MONTHS[d.getMonth() + 1];
    const day = toCnDay(d.getDate());
    const weekday = CN_WEEKDAYS[d.getDay()];
    return `${year}年${month}月${day}日 星期${weekday}`;
  }

  const day = d.getDate();
  const suffix =
    day === 1 || day === 21 || day === 31 ? 'st'
      : day === 2 || day === 22 ? 'nd'
        : day === 3 || day === 23 ? 'rd'
          : 'th';
  return d.toLocaleDateString('en-GB', { weekday: 'long' })
    + ', ' + day + suffix + ' '
    + d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

/* ── Time math ── */
interface TimeLeft { years: number; months: number; days: number; hours: number; minutes: number; seconds: number; }

function computeTimeLeft(weddingDate: string, timezone?: string): TimeLeft | 'today' | 'past' {
  const now = new Date();

  // Get "now" in the wedding timezone
  let nowYear: number, nowMonth: number, nowDay: number, nowHour: number, nowMin: number, nowSec: number;
  if (timezone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric',
      hour12: false,
    }).formatToParts(now);
    const get = (t: string) => Number(parts.find(p => p.type === t)?.value ?? 0);
    nowYear = get('year');
    nowMonth = get('month') - 1;
    nowDay = get('day');
    nowHour = get('hour');
    nowMin = get('minute');
    nowSec = get('second');
  } else {
    nowYear = now.getFullYear();
    nowMonth = now.getMonth();
    nowDay = now.getDate();
    nowHour = now.getHours();
    nowMin = now.getMinutes();
    nowSec = now.getSeconds();
  }

  const [tY, tM, tD] = weddingDate.split('-').map(Number);
  const todayStart = new Date(nowYear, nowMonth, nowDay).getTime();
  const targetStart = new Date(tY, tM - 1, tD).getTime();

  if (todayStart === targetStart) return 'today';
  if (todayStart > targetStart) return 'past';

  // Calendar-aware year/month/day diff then HMS from remainder
  let years = tY - nowYear;
  let months = (tM - 1) - nowMonth;
  let days = tD - nowDay;

  // Borrow from hours/min/sec: if current time is past midnight (i.e. partially through the day)
  // the remaining HMS until midnight of the target day
  const nowTotalSec = nowHour * 3600 + nowMin * 60 + nowSec;
  let remainSec = 86400 - nowTotalSec; // seconds left until next midnight
  if (nowTotalSec > 0) {
    days -= 1; // partial day consumed
  } else {
    remainSec = 0; // exactly midnight, no partial day
  }

  // Normalise days
  if (days < 0) {
    months -= 1;
    // Days in the month before the target month
    const prevMonth = new Date(tY, tM - 1, 0).getDate(); // last day of month before target
    days += prevMonth;
  }

  // Normalise months
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const hours = Math.floor(remainSec / 3600);
  const minutes = Math.floor((remainSec % 3600) / 60);
  const seconds = remainSec % 60;

  return { years, months, days, hours, minutes, seconds };
}

/* ── Ornament ── */
function Ornament() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, my: 2 }}>
      <Box sx={{ width: 60, height: '1px', bgcolor: 'secondary.main', opacity: 0.5 }} />
      <DiamondRingMark size={20} />
      <Box sx={{ width: 60, height: '1px', bgcolor: 'secondary.main', opacity: 0.5 }} />
    </Box>
  );
}

/* ── Single digit card ── */
function TimeCard({ value, label, pad = true }: { value: number; label: string; pad?: boolean }) {
  const prev = useRef(value);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (value !== prev.current) {
      setAnimate(true);
      prev.current = value;
      const id = setTimeout(() => setAnimate(false), 400);
      return () => clearTimeout(id);
    }
  }, [value]);

  return (
    <Box sx={{ textAlign: 'center', width: { xs: 64, sm: 100 }, flex: '0 0 auto' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: 'secondary.light',
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1.5, sm: 2.5 },
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Decorative top accent line
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '20%',
            right: '20%',
            height: 3,
            bgcolor: 'secondary.main',
            borderRadius: '0 0 4px 4px',
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontSize: { xs: 38, sm: 52 },
            fontWeight: 700,
            lineHeight: 1,
            color: 'primary.dark',
            textAlign: 'center',
            transition: 'opacity 0.3s ease, transform 0.3s ease',
            opacity: animate ? 0.4 : 1,
            transform: animate ? 'translateY(-4px)' : 'translateY(0)',
          }}
        >
          {pad ? String(value).padStart(2, '0') : String(value)}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 1,
          fontWeight: 600,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: 'text.secondary',
          fontSize: { xs: 10, sm: 12 },
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/* ── Colon separator ── */
function Separator() {
  return (
    <Typography
      sx={{
        fontFamily: '"Playfair Display", serif',
        fontSize: { xs: 28, sm: 40 },
        fontWeight: 300,
        color: 'secondary.main',
        alignSelf: 'flex-start',
        mt: { xs: 2, sm: 2.5 },
        mx: { xs: 0.5, sm: 1 },
        userSelect: 'none',
      }}
    >
      :
    </Typography>
  );
}

/* ── Main section ── */
export default function CountdownSection({ weddingDate, timezone }: CountdownSectionProps) {
  const { t, i18n } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(() => computeTimeLeft(weddingDate, timezone));

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(computeTimeLeft(weddingDate, timezone)), 1000);
    return () => clearInterval(id);
  }, [weddingDate, timezone]);

  const dateDisplay = formatWeddingDate(weddingDate, i18n.language);

  // Special states
  if (timeLeft === 'today') {
    return (
      <Box sx={{ mb: { xs: 0, sm: 5 }, mt: { xs: 0, sm: 2 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Ornament />
        <Typography
          variant="h4"
          sx={{ fontFamily: '"Playfair Display", serif', color: 'primary.dark', mb: 1 }}
        >
          🎉 {t('home.countdown.weddingDay')} 🎉
        </Typography>
        <Typography variant="h6" color="text.secondary">{dateDisplay}</Typography>
        <Ornament />
      </Box>
    );
  }

  if (timeLeft === 'past') {
    return (
      <Box sx={{ mb: { xs: 0, sm: 5 }, mt: { xs: 0, sm: 2 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Ornament />
        <Typography
          variant="h5"
          sx={{ fontFamily: '"Playfair Display", serif', color: 'primary.main' }}
        >
          {t('home.countdown.weddingPassed')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{dateDisplay}</Typography>
        <Ornament />
      </Box>
    );
  }

  const showDateRow = timeLeft.years > 0 || timeLeft.months > 0 || timeLeft.days > 0;

  return (
    <Box sx={{ mb: { xs: 0, sm: 5 }, mt: { xs: 0, sm: 2 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Ornament />

      {/* Date display */}
      <Typography
        variant="h6"
        sx={{
          fontFamily: '"Playfair Display", serif',
          color: 'primary.dark',
          mb: 3,
          letterSpacing: 1,
        }}
      >
        {dateDisplay}
      </Typography>

      {/* Countdown boxes — Row 1: dates (y/m/d), Row 2: times (h:m:s) */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 2, sm: 3 } }}>
        {/* Date row */}
        {showDateRow && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            {timeLeft.years > 0 && (
              <TimeCard value={timeLeft.years} label={t('home.countdown.years')} pad={false} />
            )}
            {timeLeft.months > 0 && (
              <TimeCard value={timeLeft.months} label={t('home.countdown.months')} pad={false} />
            )}
            {timeLeft.days > 0 && (
              <TimeCard value={timeLeft.days} label={t('home.countdown.days')} pad={false} />
            )}
          </Box>
        )}

        {/* Time row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: { xs: 0.5, sm: 0 },
          }}
        >
          {[
            ...(timeLeft.hours > 0 ? [{ key: 'hours', value: timeLeft.hours, label: t('home.countdown.hours') }] : []),
            ...(timeLeft.minutes > 0 ? [{ key: 'minutes', value: timeLeft.minutes, label: t('home.countdown.minutes') }] : []),
            { key: 'seconds', value: timeLeft.seconds, label: t('home.countdown.seconds') },
          ].map((unit, index) => (
            <Box key={unit.key} sx={{ display: 'flex', alignItems: 'flex-start' }}>
              {index > 0 && <Separator />}
              <TimeCard value={unit.value} label={unit.label} />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Subtitle */}
      <Typography
        variant="body1"
        sx={{
          mt: 3,
          color: 'text.secondary',
          fontWeight: 500,
          letterSpacing: 1,
        }}
      >
        {t('home.countdown.untilWedding')}
      </Typography>

      <Ornament />
    </Box>
  );
}
